import { useState, useEffect, useRef } from 'react';
import './OnboardingForm.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:3000'
    : 'https://grossistify-api.vercel.app'
);

interface OnboardingFormProps {
  linkId: string;
  token: string;
}

interface Driver {
  name: string;
  phone: string;
}

interface Truck {
  name: string;
  regNumber: string;
}

interface Customer {
  name: string;
  streetAddress: string;
  postalCode: string;
  city: string;
}

interface Address {
  street: string;
  postalCode: string;
  city: string;
}

interface Warehouse {
  name: string;
  sameAsCompanyAddress: boolean;
  address: Address;
  parkingAddress: string;
}

const TOTAL_STEPS = 6;

const OnboardingForm = ({ linkId, token }: OnboardingFormProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [isAnimating, setIsAnimating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLDivElement>(null);
  
  const [linkData, setLinkData] = useState<{
    companyName?: string;
    email?: string;
    expiresAt: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    companyName: '',
    orgNumber: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: { street: '', postalCode: '', city: '' } as Address,
    numberOfTrucks: 1,
    numberOfDrivers: 1,
    domain: '',
  });

  const [warehouses, setWarehouses] = useState<Warehouse[]>([{
    name: '',
    sameAsCompanyAddress: false,
    address: { street: '', postalCode: '', city: '' },
    parkingAddress: ''
  }]);
  const [drivers, setDrivers] = useState<Driver[]>([{ name: '', phone: '' }]);
  const [trucks, setTrucks] = useState<Truck[]>([{ name: 'Lastbil #1', regNumber: '' }]);
  const [customers, setCustomers] = useState<Customer[]>([{ name: '', streetAddress: '', postalCode: '', city: '' }]);

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/onboarding/${linkId}?token=${token}`);
        const data = await response.json();

        if (!response.ok) {
          setError(data.error || 'Länken är ogiltig eller har utgått');
          return;
        }

        setLinkData(data.link);
        if (data.link.companyName) {
          setFormData(prev => ({ ...prev, companyName: data.link.companyName }));
        }
        if (data.link.email) {
          setFormData(prev => ({ ...prev, email: data.link.email }));
        }
      } catch (err) {
        setError('Kunde inte ladda formuläret. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchLink();
  }, [linkId, token]);

  // Validation for each step
  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};
    
    switch (step) {
      case 1: // Företagsinformation
        if (!formData.companyName.trim()) {
          errors.companyName = 'Företagsnamn krävs';
        }
        if (!formData.orgNumber.trim()) {
          errors.orgNumber = 'Organisationsnummer krävs';
        }
        if (!formData.contactPerson.trim()) {
          errors.contactPerson = 'Kontaktperson krävs';
        }
        if (!formData.email.trim()) {
          errors.email = 'E-postadress krävs';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          errors.email = 'Ogiltig e-postadress';
        }
        if (!formData.phone.trim()) {
          errors.phone = 'Telefonnummer krävs';
        }
        break;
      case 2: // Grossistinformation - inga obligatoriska fält
        break;
      case 3: // Resurser
        if (formData.numberOfTrucks < 1) {
          errors.numberOfTrucks = 'Minst 1 lastbil krävs';
        }
        if (formData.numberOfDrivers < 1) {
          errors.numberOfDrivers = 'Minst 1 förare krävs';
        }
        break;
      // Steg 4, 5, 6 är valfria
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const goToStep = (step: number) => {
    if (step < 1 || step > TOTAL_STEPS || isAnimating) return;
    
    // Only validate when going forward
    if (step > currentStep && !validateStep(currentStep)) {
      return;
    }
    
    setDirection(step > currentStep ? 'next' : 'prev');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(step);
      setIsAnimating(false);
      setValidationErrors({});
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 300);
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      goToStep(currentStep + 1);
    }
  };
  const prevStep = () => goToStep(currentStep - 1);

  const handleDriverChange = (index: number, field: keyof Driver, value: string) => {
    const newDrivers = [...drivers];
    newDrivers[index][field] = value;
    setDrivers(newDrivers);
  };

  const addDriver = () => {
    setDrivers([...drivers, { name: '', phone: '' }]);
    setFormData(prev => ({ ...prev, numberOfDrivers: prev.numberOfDrivers + 1 }));
  };

  const removeDriver = (index: number) => {
    if (drivers.length > 1) {
      const newDrivers = drivers.filter((_, i) => i !== index);
      setDrivers(newDrivers);
      setFormData(prev => ({ ...prev, numberOfDrivers: Math.max(1, prev.numberOfDrivers - 1) }));
    }
  };

  const handleTruckChange = (index: number, field: keyof Truck, value: string) => {
    const newTrucks = [...trucks];
    newTrucks[index][field] = value;
    setTrucks(newTrucks);
  };

  const addTruck = () => {
    const newNumber = trucks.length + 1;
    setTrucks([...trucks, { name: `Lastbil #${newNumber}`, regNumber: '' }]);
    setFormData(prev => ({ ...prev, numberOfTrucks: prev.numberOfTrucks + 1 }));
  };

  const removeTruck = (index: number) => {
    if (trucks.length > 1) {
      const newTrucks = trucks.filter((_, i) => i !== index);
      setTrucks(newTrucks);
      setFormData(prev => ({ ...prev, numberOfTrucks: Math.max(1, prev.numberOfTrucks - 1) }));
    }
  };

  const handleCustomerChange = (index: number, field: keyof Customer, value: string) => {
    const newCustomers = [...customers];
    newCustomers[index][field] = value;
    setCustomers(newCustomers);
  };

  const addCustomer = () => {
    setCustomers([...customers, { name: '', streetAddress: '', postalCode: '', city: '' }]);
  };

  const removeCustomer = (index: number) => {
    if (customers.length > 1) {
      const newCustomers = customers.filter((_, i) => i !== index);
      setCustomers(newCustomers);
    }
  };

  const handleWarehouseChange = (index: number, field: keyof Warehouse, value: string | boolean | Address) => {
    const newWarehouses = [...warehouses];
    if (field === 'address' && typeof value === 'object') {
      newWarehouses[index].address = value as Address;
    } else if (field === 'sameAsCompanyAddress' && typeof value === 'boolean') {
      newWarehouses[index].sameAsCompanyAddress = value;
    } else if (typeof value === 'string') {
      (newWarehouses[index] as unknown as Record<string, unknown>)[field] = value;
    }
    setWarehouses(newWarehouses);
  };

  const handleWarehouseAddressChange = (index: number, field: keyof Address, value: string) => {
    const newWarehouses = [...warehouses];
    newWarehouses[index].address[field] = value;
    setWarehouses(newWarehouses);
  };

  const addWarehouse = () => {
    setWarehouses([...warehouses, {
      name: '',
      sameAsCompanyAddress: false,
      address: { street: '', postalCode: '', city: '' },
      parkingAddress: ''
    }]);
  };

  const removeWarehouse = (index: number) => {
    if (warehouses.length > 1) {
      const newWarehouses = warehouses.filter((_, i) => i !== index);
      setWarehouses(newWarehouses);
    }
  };

  const handleSubmit = async () => {
    // Validate all required steps before submitting
    for (let step = 1; step <= 3; step++) {
      if (!validateStep(step)) {
        setCurrentStep(step);
        return;
      }
    }
    
    setLoading(true);
    setError(null);

    try {
      // Format address for backend compatibility
      const formatAddress = (addr: Address) => {
        const parts = [addr.street, addr.postalCode, addr.city].filter(p => p.trim());
        return parts.join(', ') || null;
      };

      const response = await fetch(`${API_BASE_URL}/api/onboarding/${linkId}?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          address: formatAddress(formData.address),
          warehouses: warehouses.filter(w => w.name.trim()).map(w => ({
            name: w.name,
            address: w.sameAsCompanyAddress 
              ? formatAddress(formData.address) 
              : formatAddress(w.address),
            parkingAddress: w.parkingAddress || null
          })),
          // Keep backward compatibility with single warehouse
          warehouseName: warehouses[0]?.name || '',
          warehouseAddress: warehouses[0]?.sameAsCompanyAddress 
            ? formatAddress(formData.address) 
            : formatAddress(warehouses[0]?.address || { street: '', postalCode: '', city: '' }),
          truckParkingAddress: warehouses[0]?.parkingAddress || '',
          drivers: drivers.filter(d => d.name.trim()),
          trucks: trucks.filter(t => t.regNumber.trim()),
          customers: customers.filter(c => c.name.trim()).map(c => ({
            name: c.name,
            address: `${c.streetAddress}, ${c.postalCode} ${c.city}`.trim()
          })),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Kunde inte skicka formuläret');
        setLoading(false);
        return;
      }

      setSubmitted(true);
    } catch (err) {
      setError('Ett fel uppstod. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = [
    'Företag',
    'Grossist',
    'Resurser',
    'Lastbilar',
    'Förare',
    'Restauranger'
  ];

  const stepIcons = [
    <svg key="1" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    <svg key="2" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z"/><path d="M6 18h12"/><path d="M6 14h12"/></svg>,
    <svg key="3" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
    <svg key="4" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13" rx="2"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
    <svg key="5" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    <svg key="6" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>,
  ];

  if (loading && !linkData) {
    return (
      <div className="onboarding-page">
        <div className="onboarding-loading">
          <div className="onboarding-spinner" />
          <p>Laddar...</p>
        </div>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="onboarding-page">
        <div className="onboarding-card onboarding-error-card">
          <div className="onboarding-error-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h2>Något gick fel</h2>
          <p>{error}</p>
          <button 
            className="error-retry-btn"
            onClick={() => setError(null)}
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="onboarding-page">
        <div className="onboarding-card onboarding-success-card">
          <div className="onboarding-success-animation">
            <div className="success-circle">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path className="success-check" d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline className="success-checkmark" points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
          </div>
          <h2>Tack för din registrering!</h2>
          <p>
            Vi har tagit emot din ansökan och kommer att granska den så snart som möjligt.
            Du kommer att få ett e-postmeddelande med dina inloggningsuppgifter när kontot är klart.
          </p>
          <div className="onboarding-success-info">
            <div className="success-info-row">
              <span className="success-label">Företag</span>
              <span className="success-value">{formData.companyName}</span>
            </div>
            <div className="success-info-row">
              <span className="success-label">Kontakt</span>
              <span className="success-value">{formData.contactPerson}</span>
            </div>
            <div className="success-info-row">
              <span className="success-label">E-post</span>
              <span className="success-value">{formData.email}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <div className="step-header">
              <div className="step-icon-large">
                {stepIcons[0]}
              </div>
              <h2>Företagsinformation</h2>
              <p>Fyll i grundläggande information om ditt företag</p>
            </div>

            <div className="form-fields">
              <div className={`form-group full-width ${validationErrors.companyName ? 'has-error' : ''}`}>
                <label>Företagsnamn *</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={e => {
                    setFormData({ ...formData, companyName: e.target.value });
                    if (validationErrors.companyName) {
                      setValidationErrors(prev => ({ ...prev, companyName: '' }));
                    }
                  }}
                  placeholder="Ditt företags namn"
                />
                {validationErrors.companyName && <span className="error-text">{validationErrors.companyName}</span>}
              </div>

              <div className="form-row">
                <div className={`form-group ${validationErrors.orgNumber ? 'has-error' : ''}`}>
                  <label>Organisationsnummer *</label>
                  <input
                    type="text"
                    value={formData.orgNumber}
                    onChange={e => {
                      setFormData({ ...formData, orgNumber: e.target.value });
                      if (validationErrors.orgNumber) {
                        setValidationErrors(prev => ({ ...prev, orgNumber: '' }));
                      }
                    }}
                    placeholder="XXXXXX-XXXX"
                  />
                  {validationErrors.orgNumber && <span className="error-text">{validationErrors.orgNumber}</span>}
                </div>
                <div className={`form-group ${validationErrors.contactPerson ? 'has-error' : ''}`}>
                  <label>Kontaktperson *</label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={e => {
                      setFormData({ ...formData, contactPerson: e.target.value });
                      if (validationErrors.contactPerson) {
                        setValidationErrors(prev => ({ ...prev, contactPerson: '' }));
                      }
                    }}
                    placeholder="Förnamn Efternamn"
                  />
                  {validationErrors.contactPerson && <span className="error-text">{validationErrors.contactPerson}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className={`form-group ${validationErrors.email ? 'has-error' : ''}`}>
                  <label>E-postadress *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => {
                      setFormData({ ...formData, email: e.target.value });
                      if (validationErrors.email) {
                        setValidationErrors(prev => ({ ...prev, email: '' }));
                      }
                    }}
                    placeholder="info@foretag.se"
                  />
                  {validationErrors.email && <span className="error-text">{validationErrors.email}</span>}
                </div>
                <div className={`form-group ${validationErrors.phone ? 'has-error' : ''}`}>
                  <label>Telefonnummer *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => {
                      setFormData({ ...formData, phone: e.target.value });
                      if (validationErrors.phone) {
                        setValidationErrors(prev => ({ ...prev, phone: '' }));
                      }
                    }}
                    placeholder="08-XXX XX XX"
                  />
                  {validationErrors.phone && <span className="error-text">{validationErrors.phone}</span>}
                </div>
              </div>

              <div className="form-divider">
                <span>Företagsadress</span>
              </div>

              <div className="form-group full-width">
                <label>Gatuadress</label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={e => setFormData({ 
                    ...formData, 
                    address: { ...formData.address, street: e.target.value }
                  })}
                  placeholder="Storgatan 1"
                />
              </div>

              <div className="form-row">
                <div className="form-group postal-code">
                  <label>Postnummer</label>
                  <input
                    type="text"
                    value={formData.address.postalCode}
                    onChange={e => setFormData({ 
                      ...formData, 
                      address: { ...formData.address, postalCode: e.target.value }
                    })}
                    placeholder="123 45"
                  />
                </div>
                <div className="form-group city">
                  <label>Ort</label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={e => setFormData({ 
                      ...formData, 
                      address: { ...formData.address, city: e.target.value }
                    })}
                    placeholder="Stockholm"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label>Företagets domän (valfritt)</label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={e => setFormData({ ...formData, domain: e.target.value })}
                  placeholder="foretag.se"
                />
                <span className="form-hint">
                  Om ni har en egen domän kan vi skapa e-postadresser som @foretag.se
                </span>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <div className="step-header">
              <div className="step-icon-large">
                {stepIcons[1]}
              </div>
              <h2>Grossistinformation</h2>
              <p>Lägg till era lager och deras platsinformation</p>
            </div>

            <div className="form-fields">
              {warehouses.map((warehouse, index) => (
                <div key={index} className="item-card animate-in">
                  <div className="item-header">
                    <div className="item-badge">
                      <span className="item-number">{index + 1}</span>
                      <span className="item-type">Grossist</span>
                    </div>
                    {warehouses.length > 1 && (
                      <button 
                        type="button" 
                        className="remove-item-btn"
                        onClick={() => removeWarehouse(index)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="item-fields">
                    <div className="form-group full-width">
                      <label>Grossistnamn / Lagernamn</label>
                      <input
                        type="text"
                        value={warehouse.name}
                        onChange={e => handleWarehouseChange(index, 'name', e.target.value)}
                        placeholder="T.ex. Huvudlager Stockholm"
                      />
                    </div>

                    <div className="checkbox-card compact">
                      <label className="checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={warehouse.sameAsCompanyAddress}
                          onChange={e => handleWarehouseChange(index, 'sameAsCompanyAddress', e.target.checked)}
                        />
                        <span className="checkbox-custom">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </span>
                        <span className="checkbox-text">Samma adress som företaget</span>
                      </label>
                    </div>

                    {!warehouse.sameAsCompanyAddress && (
                      <div className="address-section animate-in">
                        <div className="form-group full-width">
                          <label>Gatuadress</label>
                          <input
                            type="text"
                            value={warehouse.address.street}
                            onChange={e => handleWarehouseAddressChange(index, 'street', e.target.value)}
                            placeholder="Industrivägen 10"
                          />
                        </div>

                        <div className="form-row">
                          <div className="form-group postal-code">
                            <label>Postnummer</label>
                            <input
                              type="text"
                              value={warehouse.address.postalCode}
                              onChange={e => handleWarehouseAddressChange(index, 'postalCode', e.target.value)}
                              placeholder="123 45"
                            />
                          </div>
                          <div className="form-group city">
                            <label>Ort</label>
                            <input
                              type="text"
                              value={warehouse.address.city}
                              onChange={e => handleWarehouseAddressChange(index, 'city', e.target.value)}
                              placeholder="Stockholm"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="form-group full-width">
                      <label>Lastbilarnas parkeringsplats</label>
                      <input
                        type="text"
                        value={warehouse.parkingAddress}
                        onChange={e => handleWarehouseChange(index, 'parkingAddress', e.target.value)}
                        placeholder="Adress där lastbilarna parkeras"
                      />
                      <span className="form-hint">
                        Ange var lastbilarna brukar stå parkerade vid denna enhet
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              <button type="button" className="add-item-btn" onClick={addWarehouse}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Lägg till grossistenhet
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <div className="step-header">
              <div className="step-icon-large">
                {stepIcons[2]}
              </div>
              <h2>Resurser</h2>
              <p>Hur många fordon och förare har ni?</p>
            </div>

            <div className="form-fields">
              <div className="resource-cards">
                <div className="resource-card">
                  <div className="resource-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="3" width="15" height="13" rx="2"/>
                      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                      <circle cx="5.5" cy="18.5" r="2.5"/>
                      <circle cx="18.5" cy="18.5" r="2.5"/>
                    </svg>
                  </div>
                  <h3>Lastbilar</h3>
                  <p>Antal fordon i flottan</p>
                  <div className="number-input-wrapper">
                    <button 
                      type="button" 
                      className="number-btn"
                      onClick={() => setFormData(prev => ({ ...prev, numberOfTrucks: Math.max(1, prev.numberOfTrucks - 1) }))}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>
                    <input
                      type="number"
                      value={formData.numberOfTrucks}
                      onChange={e => setFormData({ ...formData, numberOfTrucks: parseInt(e.target.value) || 1 })}
                      min="1"
                    />
                    <button 
                      type="button" 
                      className="number-btn"
                      onClick={() => setFormData(prev => ({ ...prev, numberOfTrucks: prev.numberOfTrucks + 1 }))}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="resource-card">
                  <div className="resource-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </div>
                  <h3>Förare</h3>
                  <p>Antal anställda förare</p>
                  <div className="number-input-wrapper">
                    <button 
                      type="button" 
                      className="number-btn"
                      onClick={() => setFormData(prev => ({ ...prev, numberOfDrivers: Math.max(1, prev.numberOfDrivers - 1) }))}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>
                    <input
                      type="number"
                      value={formData.numberOfDrivers}
                      onChange={e => setFormData({ ...formData, numberOfDrivers: parseInt(e.target.value) || 1 })}
                      min="1"
                    />
                    <button 
                      type="button" 
                      className="number-btn"
                      onClick={() => setFormData(prev => ({ ...prev, numberOfDrivers: prev.numberOfDrivers + 1 }))}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                <p>Du kan lägga till detaljer om specifika lastbilar och förare i nästa steg</p>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <div className="step-header">
              <div className="step-icon-large">
                {stepIcons[3]}
              </div>
              <h2>Lastbilar</h2>
              <p>Lägg till namn och registreringsnummer (valfritt)</p>
            </div>

            <div className="form-fields">
              <div className="items-list">
                {trucks.map((truck, index) => (
                  <div key={index} className="item-row" style={{ animationDelay: `${index * 0.05}s` }}>
                    <div className="item-number">{index + 1}</div>
                    <div className="item-inputs">
                      <input
                        type="text"
                        value={truck.name}
                        onChange={e => handleTruckChange(index, 'name', e.target.value)}
                        placeholder={`Lastbil #${index + 1}`}
                        className="truck-name-input"
                      />
                      <input
                        type="text"
                        value={truck.regNumber}
                        onChange={e => handleTruckChange(index, 'regNumber', e.target.value.toUpperCase())}
                        placeholder="ABC 123"
                        className="truck-reg-input"
                      />
                    </div>
                    {trucks.length > 1 && (
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeTruck(index)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button type="button" className="add-item-btn" onClick={addTruck}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Lägg till lastbil
              </button>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <div className="step-header">
              <div className="step-icon-large">
                {stepIcons[4]}
              </div>
              <h2>Förare</h2>
              <p>Lägg till namn och telefonnummer så skapar vi konton åt dem (valfritt)</p>
            </div>

            <div className="form-fields">
              <div className="items-list">
                {drivers.map((driver, index) => (
                  <div key={index} className="item-row" style={{ animationDelay: `${index * 0.05}s` }}>
                    <div className="item-number">{index + 1}</div>
                    <div className="item-inputs">
                      <input
                        type="text"
                        value={driver.name}
                        onChange={e => handleDriverChange(index, 'name', e.target.value)}
                        placeholder="Förarens namn"
                      />
                      <input
                        type="tel"
                        value={driver.phone}
                        onChange={e => handleDriverChange(index, 'phone', e.target.value)}
                        placeholder="Telefonnummer"
                      />
                    </div>
                    {drivers.length > 1 && (
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeDriver(index)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button type="button" className="add-item-btn" onClick={addDriver}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Lägg till förare
              </button>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="step-content">
            <div className="step-header">
              <div className="step-icon-large">
                {stepIcons[5]}
              </div>
              <h2>Era restauranger</h2>
              <p>Lägg till era kunder och deras leveransadresser (valfritt)</p>
            </div>

            <div className="form-fields">
              <div className="items-list">
                {customers.map((customer, index) => (
                  <div key={index} className="customer-card" style={{ animationDelay: `${index * 0.05}s` }}>
                    <div className="customer-header">
                      <div className="item-number">{index + 1}</div>
                      <span className="customer-label">Restaurang</span>
                      {customers.length > 1 && (
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => removeCustomer(index)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      )}
                    </div>
                    <div className="customer-fields">
                      <div className="form-group full-width">
                        <label>Restaurangnamn</label>
                        <input
                          type="text"
                          value={customer.name}
                          onChange={e => handleCustomerChange(index, 'name', e.target.value)}
                          placeholder="T.ex. Restaurang Solsidan"
                        />
                      </div>
                      <div className="form-group full-width">
                        <label>Gatuadress</label>
                        <input
                          type="text"
                          value={customer.streetAddress}
                          onChange={e => handleCustomerChange(index, 'streetAddress', e.target.value)}
                          placeholder="Restaurangvägen 1"
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group postal-code">
                          <label>Postnummer</label>
                          <input
                            type="text"
                            value={customer.postalCode}
                            onChange={e => handleCustomerChange(index, 'postalCode', e.target.value)}
                            placeholder="123 45"
                          />
                        </div>
                        <div className="form-group city">
                          <label>Ort</label>
                          <input
                            type="text"
                            value={customer.city}
                            onChange={e => handleCustomerChange(index, 'city', e.target.value)}
                            placeholder="Stockholm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" className="add-item-btn" onClick={addCustomer}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Lägg till restaurang
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        <div className="onboarding-header">
          <div className="onboarding-logo">
            <div className="onboarding-logo-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <span>Grossistify</span>
          </div>
        </div>

        <div className="progress-section">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
            />
          </div>
          <div className="progress-steps">
            {stepTitles.map((title, index) => (
              <button
                key={index}
                type="button"
                className={`progress-step ${currentStep === index + 1 ? 'active' : ''} ${currentStep > index + 1 ? 'completed' : ''}`}
                onClick={() => goToStep(index + 1)}
              >
                <div className="step-dot">
                  {currentStep > index + 1 ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <span className="step-title">{title}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="onboarding-card" ref={formRef}>
          <div className={`slide-container ${isAnimating ? `slide-${direction}-exit` : 'slide-enter'}`}>
            {renderStepContent()}
          </div>

          <div className="navigation-buttons">
            {currentStep > 1 && (
              <button type="button" className="nav-btn prev-btn" onClick={prevStep}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="19" y1="12" x2="5" y2="12"/>
                  <polyline points="12 19 5 12 12 5"/>
                </svg>
                Tillbaka
              </button>
            )}
            
            <div className="nav-spacer" />

            {currentStep < TOTAL_STEPS ? (
              <button type="button" className="nav-btn next-btn" onClick={nextStep}>
                Fortsätt
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </button>
            ) : (
              <button 
                type="button" 
                className="nav-btn submit-btn" 
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="btn-spinner" />
                    Skickar...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13"/>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                    Skicka registrering
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="onboarding-footer">
          <p>Genom att skicka in godkänner du att vi behandlar dina uppgifter.</p>
          <p>© 2026 Grossistify. Alla rättigheter förbehållna.</p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingForm;
